import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography,
  IconButton, Button, CircularProgress, Divider,
} from '@mui/material';
import { Close, LocalOffer, Schedule, CheckCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useCancelApprovalRequestMutation, useLazyGetPosApprovalRequestQuery } from '../../../services/discountApi';
import { useHoldOrderMutation, useLazyGetOrderQuery } from '../../../services/orderApi';
import { setCurrentOrder, clearOrder } from '../../../store/orderSlice';
import { clearPendingApproval, handOffPendingApproval } from '../../../store/discountSlice';
import { useDiscountOtpCycle } from '../../../hooks/useDiscountOtpCycle';
import { useToast } from '../../common/ToastProvider';
import DiscountOtpCodeEntry from './DiscountOtpCodeEntry';

// Primary path — centered modal shown the instant a discount needs approval
// (docs/ui-ux/discount-approval-otp.md §2.1). Mounted at Dashboard.js level,
// self-driven off discountSlice (pendingApprovalRequestId + awaitingInline),
// same pattern as ApprovalBanner.js for the fallback path. Reuses the exact
// same Dialog shell as the fallback-path DiscountOtpModal.js — covers the
// whole awaiting-approval interaction (not-started, active cycle, resolved),
// not just code entry.
const DiscountApprovalModal = ({ onPendingApproval }) => {
  const dispatch = useDispatch();
  const showToast = useToast();

  const {
    pendingApprovalRequestId, pendingApprovalDiscountName, pendingOrderId, awaitingInline,
  } = useSelector((s) => s.discount);
  const open = awaitingInline && !!pendingApprovalRequestId;
  const otp = useDiscountOtpCycle(pendingApprovalRequestId);

  const [holdOrder] = useHoldOrderMutation();
  const [triggerGetOrder] = useLazyGetOrderQuery();
  const [triggerGetApproval] = useLazyGetPosApprovalRequestQuery();
  const [cancelApprovalRequest, { isLoading: isCancelling }] = useCancelApprovalRequestMutation();

  // Resolution can come from OTP verify (via the shared hook) or a manager
  // approving straight from the admin dashboard, which only the poll below
  // catches. Local state (not otp.view) is the single place both land.
  const [resolved, setResolved] = useState(false);
  const [resolvedByName, setResolvedByName] = useState('');

  // Explicit hand-off to the fallback path (ApprovalBanner). The backend
  // doesn't hold the order automatically when approval is required — that's
  // this action's job. See DiscountApplicationService#call (apex_v3).
  const handleContinueBrowsing = async () => {
    if (pendingOrderId) {
      try {
        await holdOrder(pendingOrderId).unwrap();
      } catch {
        // Already on_hold (e.g. a second hand-off) or otherwise unholdable —
        // nothing else to do either way, the approval request itself is untouched.
      }
    }
    dispatch(handOffPendingApproval());
    dispatch(clearOrder());
    onPendingApproval?.();
  };

  const handleCancelRequest = async () => {
    if (!pendingApprovalRequestId) return;
    try {
      await cancelApprovalRequest(pendingApprovalRequestId).unwrap();
      dispatch(clearPendingApproval());
      showToast('Approval request cancelled', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to cancel approval request', 'error');
    }
  };

  useEffect(() => {
    if (otp.view === 'resolved' && !resolved) {
      // eslint-disable-next-line
      setResolved(true);
      setResolvedByName(otp.resolvedByName);
    }
    // eslint-disable-next-line
  }, [otp.view, otp.resolvedByName]);

  // Polls the request itself while open, same as ApprovalBanner's own poll
  // for the fallback path — catches a manager approving/rejecting straight
  // from the admin dashboard instead of via OTP.
  useEffect(() => {
    if (!open || resolved) return undefined;
    let stopped = false;

    const poll = async () => {
      try {
        const res = await triggerGetApproval(pendingApprovalRequestId, true).unwrap();
        const req = res?.data?.discount_approval_request;
        if (!req || stopped) return;

        if (req.status === 'approved') {
          stopped = true;
          setResolved(true);
          setResolvedByName(req.approved_by_name || '');
        } else if ([ 'rejected', 'cancelled', 'expired' ].includes(req.status)) {
          stopped = true;
          dispatch(clearPendingApproval());
          showToast(
            req.status === 'rejected' ? 'Discount request rejected by manager' : `Discount request ${req.status}`,
            'error',
          );
        }
      } catch {
        // silent — keep polling
      }
    };

    poll();
    const interval = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(interval); };
    // eslint-disable-next-line
  }, [open, pendingApprovalRequestId, resolved]);

  // Refresh the (never-cleared) order once resolved, then close — no
  // hand-off, no banner, the cashier was never taken off this bill.
  useEffect(() => {
    if (!resolved) return undefined;
    let cancelled = false;

    (async () => {
      if (pendingOrderId) {
        try {
          const res = await triggerGetOrder(pendingOrderId).unwrap();
          const order = res?.data?.order || res?.data;
          if (order && !cancelled) dispatch(setCurrentOrder(order));
        } catch {
          // order refresh is best-effort — the discount was already applied server-side
        }
      }
    })();

    const t = setTimeout(() => {
      if (cancelled) return;
      dispatch(clearPendingApproval());
      setResolved(false);
      setResolvedByName('');
    }, 1200);

    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [resolved]);

  const noFastPath = otp.view === 'no_approver' || otp.view === 'exhausted'
    || (otp.view === 'expired' && !otp.canStartNewCycle);

  return (
    <Dialog
      open={open}
      onClose={handleContinueBrowsing}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', m: { xs: 2, sm: 3 } } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOffer sx={{ color: '#4F46E5', fontSize: 18 }} />
          <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B">Discount Approval</Typography>
        </Box>
        {!resolved && (
          <IconButton size="small" onClick={handleContinueBrowsing} sx={{ color: '#94A3B8', mr: -0.5 }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        {resolved ? (
          <Box sx={{ py: 1, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 32, color: '#16A34A', mb: 1 }} />
            <Typography fontWeight={800} fontSize="0.9rem" color="#166534">
              Approved by {resolvedByName || 'manager'}
            </Typography>
          </Box>
        ) : otp.view === 'active' ? (
          <DiscountOtpCodeEntry
            cycle={otp.cycle}
            secondsLeft={otp.secondsLeft}
            resendCooldown={otp.resendCooldown}
            isResending={otp.isResending}
            isVerifying={otp.isVerifying}
            onVerify={otp.verify}
            onResend={otp.resend}
          />
        ) : (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Schedule sx={{ fontSize: 18, color: '#F59E0B', mt: 0.2, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} fontSize="0.82rem" color="#1E293B">
                  {pendingApprovalDiscountName || 'Discount'} needs manager approval
                </Typography>
                <Typography fontSize="0.72rem" color="#64748B" sx={{ mt: 0.25 }}>
                  {noFastPath
                    ? 'This org resolves discount approvals from the admin dashboard only — no in-store SMS option right now.'
                    : otp.view === 'expired'
                      ? `Code expired — cycle ${otp.cycle?.cycleNumber} of 3 used`
                      : 'Order total exceeds your discount limit.'}
                </Typography>
              </Box>
            </Box>

            {!noFastPath && otp.view === 'idle' && (
              <Button
                fullWidth variant="outlined" onClick={otp.start} disabled={otp.isStarting}
                sx={{ mt: 2, fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#4F46E5', color: '#4F46E5' }}
              >
                {otp.isStarting ? <CircularProgress size={16} /> : 'Text a manager for OTP'}
              </Button>
            )}
            {!noFastPath && otp.view === 'expired' && otp.canStartNewCycle && (
              <Button
                fullWidth variant="outlined" onClick={otp.start} disabled={otp.isStarting}
                sx={{ mt: 2, fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#4F46E5', color: '#4F46E5' }}
              >
                {otp.isStarting ? <CircularProgress size={16} /> : 'New cycle'}
              </Button>
            )}
          </Box>
        )}

        {!resolved && (
          <Box sx={{ borderTop: '1px solid #F1F5F9', pt: 1.5, mt: 2 }}>
            {!noFastPath && (
              <Typography fontSize="0.68rem" color="#94A3B8" sx={{ mb: 1 }}>
                Can't wait? A manager can also approve this later from the admin dashboard.
              </Typography>
            )}
            <Button
              fullWidth variant="text" onClick={handleContinueBrowsing}
              sx={{ fontWeight: 700, textTransform: 'none', color: '#64748B' }}
            >
              Continue browsing — I'll come back to this
            </Button>
          </Box>
        )}
      </DialogContent>

      {!resolved && (
        <>
          <Divider />
          <Box sx={{ px: 2.5, py: 2 }}>
            <Button
              fullWidth
              onClick={handleCancelRequest}
              disabled={isCancelling}
              sx={{ fontWeight: 700, textTransform: 'none', color: '#DC2626', '&:hover': { bgcolor: '#FEF2F2' } }}
            >
              {isCancelling ? <CircularProgress size={16} color="error" /> : 'Cancel request'}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default DiscountApprovalModal;
